// Parsing/labels de reward compartilhados entre o card antigo
// (components/user/rewards/Item.vue) e o novo card do Minha Conta V2
// (components/user-v2/RewardCard.vue) — evita duplicar a mesma regra em
// dois lugares.
import { isRunningInNativeApp } from '~/utils/browserInfo';

// ENG-799: bônus marcado pela API como resgatável só pelo app. Fora do app
// o resgate fica travado e o card vira o convite pra baixar; DENTRO do app
// o bônus é um bônus comum.
//
// Chamada direta (sem `onMounted`) é segura aqui: `nuxt.config.ts` roda com
// `ssr: false`, então não existe hydration mismatch, e o react-native-webview
// injeta `window.ReactNativeWebView` ANTES do JS da página — o mesmo
// raciocínio já documentado em `composables/useAppTrackingPolicy.ts`.
export function isRewardAppOnlyBlocked(reward: Reward): boolean {
  return !!reward.is_app_only && !isRunningInNativeApp();
}

// player_desc chega como "Giros grátis Tigre Sortudo {R$ 0,05}": o valor
// (quando existe) vem embutido entre chaves, resto é a descrição exibida.
export function parseRewardDescription(playerDesc?: string) {
  const match = playerDesc?.match(/{([^}]+)}/);

  return {
    value: match?.[1]?.trim() ?? null,
    description: playerDesc?.replace(/{[^}]+}/, '').trim() || '',
  };
}

// tom semântico por status — único lugar que sabe "expired é vermelho,
// redeemed é verde" etc.; os dois design systems abaixo só traduzem o tom
// pras próprias classes.
type RewardTone = 'primary' | 'success' | 'error' | 'warning' | 'gold';

const STATUS_CONFIG: Record<
  RewardStatus,
  { text: string; tone: RewardTone; icon: string }
> = {
  available: { text: 'Disponível', tone: 'primary', icon: 'clock' },
  redeemed: { text: 'Resgatado', tone: 'success', icon: 'check' },
  auto_redeemed: {
    text: 'Resgate automático',
    tone: 'success',
    icon: 'check',
  },
  expired: { text: 'Expirado', tone: 'error', icon: 'timer' },
  canceled: { text: 'Cancelado', tone: 'error', icon: 'close' },
};

function getStatusConfig(status: RewardStatus) {
  // status é um union fechado (types/rewards.d.ts) — o fallback é só rede de
  // segurança caso a API mande algo fora do tipo.
  return (
    STATUS_CONFIG[status] ?? {
      text: status,
      tone: 'warning' as const,
      icon: 'info',
    }
  );
}

// classes por tom, escritas por extenso — Tailwind escaneia string literal
// no source, não dá pra montar "text-" + tom em runtime (o JIT simplesmente
// não geraria a classe).
const LEGACY_TONE_CLASSES: Record<
  RewardTone,
  { color: string; bg: string; border: string }
> = {
  primary: {
    color: 'text-primary',
    bg: 'bg-primary/5',
    border: 'border-primary/10',
  },
  success: {
    color: 'text-success',
    bg: 'bg-success/5',
    border: 'border-success/10',
  },
  error: { color: 'text-error', bg: 'bg-error/5', border: 'border-error/10' },
  warning: {
    color: 'text-warning',
    bg: 'bg-warning/5',
    border: 'border-warning/10',
  },
  // ENG-799: `accent`/`accent-text` (#5c4214 / #ffe800) já existem na paleta
  // legada — `tailwind.config.js` é caminho restrito pelo ruleset da org, então
  // token novo aqui não é opção.
  gold: {
    color: 'text-accent-text',
    bg: 'bg-accent/40',
    border: 'border-accent-text/30',
  },
};

// mesmo tom do LEGACY_TONE_CLASSES acima, só que nos tokens v2-* — usada
// pelo card novo (components/user-v2/RewardCard.vue), que é 100% v2-* e não
// pode puxar cor do design system antigo. "error" legado = "danger" na v2
// (nomes diferentes pro mesmo tom em cada sistema).
const V2_TONE_CLASSES: Record<
  RewardTone,
  { color: string; bg: string; border: string }
> = {
  primary: {
    color: 'text-v2-primary',
    bg: 'bg-v2-primary/10',
    border: 'border-v2-primary/20',
  },
  success: {
    color: 'text-v2-success',
    bg: 'bg-v2-success/10',
    border: 'border-v2-success/20',
  },
  error: {
    color: 'text-v2-danger',
    bg: 'bg-v2-danger/10',
    border: 'border-v2-danger/20',
  },
  warning: {
    color: 'text-v2-warning',
    bg: 'bg-v2-warning/10',
    border: 'border-v2-warning/20',
  },
  // ENG-799: token `--v2-gold`, registrado em `modules/userV2Theme.ts` +
  // `assets/css/user-v2-tokens.scss` (o hook do módulo é o caminho pra
  // adicionar cor, já que tailwind.config.js é restrito).
  gold: {
    color: 'text-v2-gold',
    bg: 'bg-v2-gold/10',
    border: 'border-v2-gold/30',
  },
};

// ENG-799: em Finalizados, o bônus app-only resgatado à mão diz que foi
// resgatado PELO APP. `auto_redeemed` fica de fora de propósito — resgate
// automático não aconteceu pelo app, e dizer isso seria falso. `expired` e
// `canceled` também não mudam: o prêmio morreu, de onde veio não importa.
//
// Repare que aqui o gatilho é `is_app_only` puro, SEM `isRunningInNativeApp()`:
// histórico não pode trocar de texto conforme onde o jogador o abre.
function getAppOnlyStatusConfig(status: RewardStatus, isAppOnly: boolean) {
  if (isAppOnly && status === 'redeemed') {
    return {
      text: 'Resgatado pelo app',
      tone: 'gold' as const,
      icon: 'check',
    };
  }

  return getStatusConfig(status);
}

export function getRewardStatus(status: RewardStatus, isAppOnly = false) {
  const { text, tone, icon } = getAppOnlyStatusConfig(status, isAppOnly);

  return { text, icon, ...LEGACY_TONE_CLASSES[tone] };
}

export function getRewardStatusV2(status: RewardStatus, isAppOnly = false) {
  const { text, tone, icon } = getAppOnlyStatusConfig(status, isAppOnly);

  return { text, icon, ...V2_TONE_CLASSES[tone] };
}
